import { t } from "i18next";
import {
    RECT_FOR_CORNER_SHOW_SIZE,
    RECT_FOR_CORNER_SIZE,
    VOTE_TOOLTIP_HEIGHT,
    VOTE_TOOLTIP_MARGIN,
    VOTE_TOOLTIP_WIDTH,
} from "@/appInterface/windows/VoteWindow/VoteScreen";
import { canv } from "@/canv/global";
import { getViewport } from "@/canvasController/helpers";
import { getScaledSize } from "@/utils";
import {
    DEFAULT_SELECTION_HEIGHT_RATIO,
    DEFAULT_SELECTION_MARGIN,
    DEFAULT_SELECTION_WIDTH_RATIO,
} from "./constants";

export type ImageExportArea = {
    startX: number;
    startY: number;
    width: number;
    height: number;
};

export type ImageExportCorner = "leftTop" | "rightTop" | "leftBottom" | "rightBottom";

export class ImageExportScreen {
    create() {
        this.setDefaultArea();
        this.update();
    }

    remove() {
        canv.redraw();
    }

    getArea(): ImageExportArea {
        return {
            startX: this.startX,
            startY: this.startY,
            width: this.width,
            height: this.height,
        };
    }

    getRatio() {
        const viewPort = getViewport();

        return canv.canvas.width / viewPort.viewportWidth;
    }

    getCornersCoords() {
        const ratio = this.getRatio();
        const fullSize = RECT_FOR_CORNER_SIZE / ratio;
        const cutSize = RECT_FOR_CORNER_SHOW_SIZE / ratio;

        return {
            leftTop: {
                startX: this.startX - RECT_FOR_CORNER_SHOW_SIZE / ratio,
                startY: this.startY - RECT_FOR_CORNER_SHOW_SIZE / ratio,
            },
            rightTop: {
                startX:
                    this.startX +
                    this.width -
                    (RECT_FOR_CORNER_SIZE - RECT_FOR_CORNER_SHOW_SIZE) / ratio,
                startY: this.startY - RECT_FOR_CORNER_SHOW_SIZE / ratio,
            },
            leftBottom: {
                startX: this.startX - RECT_FOR_CORNER_SHOW_SIZE / ratio,
                startY:
                    this.startY +
                    this.height -
                    (RECT_FOR_CORNER_SIZE - RECT_FOR_CORNER_SHOW_SIZE) / ratio,
            },
            rightBottom: {
                startX:
                    this.startX +
                    this.width -
                    (RECT_FOR_CORNER_SIZE - RECT_FOR_CORNER_SHOW_SIZE) / ratio,
                startY:
                    this.startY +
                    this.height -
                    (RECT_FOR_CORNER_SIZE - RECT_FOR_CORNER_SHOW_SIZE) / ratio,
            },
            fullSize,
            cutSize,
        };
    }

    update() {
        canv.redraw();
    }

    translate(x: number, y: number) {
        this.startX += x;
        this.startY += y;

        this.update();
    }

    resize(catchPointer: ImageExportCorner) {
        const pt = canv.actionManager.getActionPoint();
        const { fullSize } = this.getCornersCoords();

        let startX = this.startX;
        let startY = this.startY;
        let width = this.width;
        let height = this.height;

        if (catchPointer === "leftTop") {
            startX = pt.x;
            width = this.startX + this.width - startX;
            startY = pt.y;
            height = this.startY + this.height - startY;
        } else if (catchPointer === "rightTop") {
            startY = pt.y;
            height = this.startY + this.height - startY;
            width = pt.x - this.startX;
        } else if (catchPointer === "leftBottom") {
            startX = pt.x;
            width = this.startX + this.width - startX;
            height = pt.y - this.startY;
        } else if (catchPointer === "rightBottom") {
            width = pt.x - this.startX;
            height = pt.y - this.startY;
        }

        if (width >= fullSize && height >= fullSize) {
            this.startX = startX;
            this.startY = startY;
            this.width = width;
            this.height = height;

            this.update();
        }
    }

    overCorner(): ImageExportCorner | null {
        const pt = canv.actionManager.getActionPoint();
        const { leftTop, rightTop, leftBottom, rightBottom, fullSize } = this.getCornersCoords();

        if (
            pt.x > leftTop.startX &&
            pt.y > leftTop.startY &&
            pt.x < leftTop.startX + fullSize &&
            pt.y < leftTop.startY + fullSize
        ) {
            return "leftTop";
        }

        if (
            pt.x > rightTop.startX &&
            pt.y > rightTop.startY &&
            pt.x < rightTop.startX + fullSize &&
            pt.y < rightTop.startY + fullSize
        ) {
            return "rightTop";
        }

        if (
            pt.x > leftBottom.startX &&
            pt.y > leftBottom.startY &&
            pt.x < leftBottom.startX + fullSize &&
            pt.y < leftBottom.startY + fullSize
        ) {
            return "leftBottom";
        }

        if (
            pt.x > rightBottom.startX &&
            pt.y > rightBottom.startY &&
            pt.x < rightBottom.startX + fullSize &&
            pt.y < rightBottom.startY + fullSize
        ) {
            return "rightBottom";
        }

        return null;
    }

    isOverRect() {
        const pt = canv.actionManager.getActionPoint();

        return (
            pt.x > this.startX &&
            pt.y > this.startY &&
            pt.x < this.startX + this.width &&
            pt.y < this.startY + this.height
        );
    }

    drawBackground() {
        const viewPort = getViewport();

        canv.ctx.save();

        canv.ctx.beginPath();
        canv.ctx.rect(this.startX, this.startY, this.width, this.height);
        canv.ctx.rect(
            viewPort.viewportStartX,
            viewPort.viewportStartY,
            viewPort.viewportWidth,
            viewPort.viewportHeight,
        );
        canv.ctx.fillStyle = "rgba(0, 0, 0, 0.4)";
        canv.ctx.fill("evenodd");

        canv.ctx.restore();
    }

    drawCorner() {
        const { leftTop, rightTop, leftBottom, rightBottom, cutSize, fullSize } =
            this.getCornersCoords();

        canv.ctx.save();

        canv.ctx.fillStyle = "rgba(44, 45, 50, 1)";

        canv.ctx.fillRect(leftTop.startX, leftTop.startY, fullSize, cutSize);
        canv.ctx.fillRect(leftTop.startX, leftTop.startY, cutSize, fullSize);

        canv.ctx.fillRect(rightTop.startX, rightTop.startY, fullSize, cutSize);
        canv.ctx.fillRect(
            rightTop.startX + (fullSize - cutSize),
            rightTop.startY,
            cutSize,
            fullSize,
        );

        canv.ctx.fillRect(leftBottom.startX, leftBottom.startY, cutSize, fullSize);
        canv.ctx.fillRect(
            leftBottom.startX,
            leftBottom.startY + (fullSize - cutSize),
            fullSize,
            cutSize,
        );

        canv.ctx.fillRect(
            rightBottom.startX,
            rightBottom.startY + (fullSize - cutSize),
            fullSize,
            cutSize,
        );
        canv.ctx.fillRect(
            rightBottom.startX + (fullSize - cutSize),
            rightBottom.startY,
            cutSize,
            fullSize,
        );

        canv.ctx.restore();
    }

    drawTooltip() {
        const startX = this.startX + getScaledSize(VOTE_TOOLTIP_MARGIN);
        const startY = this.startY + getScaledSize(VOTE_TOOLTIP_MARGIN);
        const width = getScaledSize(VOTE_TOOLTIP_WIDTH);
        const height = getScaledSize(VOTE_TOOLTIP_HEIGHT);
        const fontSize = (getScaledSize(9) * canv.dpr) / 1.5;

        canv.ctx.save();

        canv.ctx.beginPath();
        canv.ctx.fillStyle = "rgba(44, 45, 50, 1)";
        canv.ctx.roundRect(startX, startY, width, height, getScaledSize(8));
        canv.ctx.fill();

        canv.ctx.font = `normal ${fontSize.toFixed(4)}px Rubik`;
        canv.ctx.fillStyle = "rgba(250, 250, 250, 1)";
        canv.ctx.textAlign = "center";
        canv.ctx.textBaseline = "middle";
        canv.ctx.fillText(t("export.exportArea"), startX + width / 2, startY + height / 2);

        canv.ctx.restore();
    }

    draw() {
        canv.ctx.save();

        canv.ctx.fillStyle = "#fff";
        canv.ctx.fillRect(this.startX, this.startY, this.width, this.height);

        canv.ctx.restore();
    }

    private setDefaultArea() {
        const viewPort = getViewport();
        const margin = this.getWorldSize(DEFAULT_SELECTION_MARGIN);
        const { fullSize } = this.getCornersCoords();
        const minSize = fullSize;
        const maxWidth = Math.max(minSize, viewPort.viewportWidth - margin * 2);
        const maxHeight = Math.max(minSize, viewPort.viewportHeight - margin * 2);

        this.width = Math.max(
            minSize,
            Math.min(maxWidth, viewPort.viewportWidth * DEFAULT_SELECTION_WIDTH_RATIO),
        );
        this.height = Math.max(
            minSize,
            Math.min(maxHeight, viewPort.viewportHeight * DEFAULT_SELECTION_HEIGHT_RATIO),
        );
        this.startX = viewPort.viewportStartX + (viewPort.viewportWidth - this.width) / 2;
        this.startY = viewPort.viewportStartY + (viewPort.viewportHeight - this.height) / 2;
    }

    private getWorldSize(size: number) {
        return (size * canv.dpr) / canv.m[0];
    }

    startX = 0;
    startY = 0;
    width = 0;
    height = 0;
}
